import useOrganisationsDatabase from "~/composables/supabase/useOrganisationsDatabase";
import { OrganizationRole } from "~/types/enums";
import type { OrganisationWithMembers } from "~/types/organisations/types";
import type { Tables } from "~/types/types.gen";

export const useOrganisationStore = defineStore("organisationStore", {
  state: () => ({
    organisations: [] as OrganisationWithMembers[],
    usersOrganisations: [] as OrganisationWithMembers[],
    usersOrganisationsFetchInProgress: false,
  }),
  getters: {
    storageFolderName: () => "organisations",
    usersOrganisationsWhereUserIsOwner: (state) => {
      return state.usersOrganisations.filter((org) => {
        return org.members?.some(
          (member) =>
            member.user_id === useUserStore().user?.id &&
            member.role === OrganizationRole.OWNER,
        );
      });
    },

    isUserMemberOfOrganisation: (state) => {
      return (organisationId: string): boolean => {
        const organisation = state.organisations.find(
          (org) => org.id === organisationId,
        );
        if (!organisation) {
          return false;
        }
        const userId = useUserStore().user?.id;
        return (
          organisation.members?.some((member) => member.user_id === userId) ??
          false
        );
      };
    },
  },
  actions: {
    async fetchUsersOrganisations() {
      if (
        !useUserStore().isUserLoggedIn ||
        !useUserStore().isOrganizer ||
        this.usersOrganisations.length > 0 ||
        this.usersOrganisationsFetchInProgress
      ) {
        return;
      }

      try {
        this.usersOrganisationsFetchInProgress = true;
        const data = await useOrganisationsDatabase().fetchUsersOrganisations();

        // Only add organisations that are not already present, or update existing ones
        for (const org of data) {
          const index = this.organisations.findIndex(
            (existingOrg) => existingOrg.id === org.id,
          );
          if (index === -1) {
            this.organisations.push(org);
          } else {
            this.organisations[index] = org;
          }

          const usersIndex = this.usersOrganisations.findIndex(
            (existingOrg) => existingOrg.id === org.id,
          );
          if (usersIndex === -1) {
            this.usersOrganisations.push(org);
          } else {
            this.usersOrganisations[usersIndex] = org;
          }
        }

        this.sortUsersOrganisations();
      } catch (error) {
        throw error;
      } finally {
        this.usersOrganisationsFetchInProgress = false;
      }
    },

    async createOrganisation(organisation: Tables<"organisations">) {
      const { data, error: createError } = await useFetch(
        "/api/organisations",
        {
          method: HttpMethod.POST,
          body: organisation,
        },
      );

      const error = data.value?.error || createError.value;
      if (error) {
        usePosthog().posthogLogError({
          message: "Failed to create organisation",
          error,
        });
        throw error;
      }

      const newOrganisation = data.value?.data as OrganisationWithMembers;
      if (newOrganisation) {
        this.organisations.push(newOrganisation);
        this.usersOrganisations.push(newOrganisation);
      }
      this.sortUsersOrganisations();
    },

    async updateOrganisation(
      updatedData: Partial<Tables<"organisations">>,
      options?: { confirmVerificationInvalidation?: boolean },
    ): Promise<OrganisationWithMembers> {
      const { data, error: updateError } = await useFetch(
        `/api/organisations`,
        {
          method: HttpMethod.PUT,
          body: {
            ...updatedData,
            ...(options?.confirmVerificationInvalidation
              ? { confirmVerificationInvalidation: true }
              : {}),
          },
        },
      );

      const error = data.value?.error || updateError.value;
      if (error) {
        if (
          useVerificationEntityEditGuard().isVerificationEditConfirmationError(
            error,
          )
        ) {
          throw error;
        }

        usePosthog().posthogLogError({
          message: "Failed to update organisation",
          error,
          context: {
            userId: useUserStore().user?.id,
            action: "updateOrganisation",
          },
        });
        throw error;
      }

      const updatedOrganisation = data.value?.data;

      if (updatedOrganisation) {
        const index = this.organisations.findIndex(
          (org) => org.id === updatedData.id,
        );
        if (index !== -1) {
          this.organisations[index] = updatedOrganisation;
        }

        const usersIndex = this.usersOrganisations.findIndex(
          (org) => org.id === updatedData.id,
        );
        if (usersIndex !== -1) {
          this.usersOrganisations[usersIndex] = updatedOrganisation;
        }

        this.sortUsersOrganisations();

        return updatedOrganisation;
      }

      usePosthog().posthogLogError({
        message: "No data returned",
        error,
        context: {
          userId: useUserStore().user?.id,
          action: "updateOrganisation",
        },
      });

      throw new Error("Organisation update failed: No data returned");
    },

    async deleteOrganisation(organisationId: string) {
      try {
        await $fetch(`/api/organisations`, {
          method: HttpMethod.DELETE,
          params: { id: organisationId },
        });

        this.organisations = this.organisations.filter(
          (org) => org.id !== organisationId,
        );

        this.usersOrganisations = this.usersOrganisations.filter(
          (org) => org.id !== organisationId,
        );

        this.sortUsersOrganisations();
      } catch (error) {
        usePosthog().posthogLogError({
          message: "Failed to delete organisation",
          error,
        });
        throw error;
      }
    },

    async getOrganisationById(
      organisationId: string | null | undefined,
    ): Promise<OrganisationWithMembers | undefined> {
      if (!organisationId) {
        return undefined;
      }

      const organisation =
        this.organisations.find((org) => org.id === organisationId) ??
        this.usersOrganisations.find((org) => org.id === organisationId);

      if (organisation && organisation.members !== undefined) {
        return organisation;
      }

      try {
        const { data, error } = await usePrivateApiFetch()(
          `/api/organisations`,
          {
            params: { id: organisationId },
          },
        );

        if (error) {
          throw error;
        }

        if (data && data.length > 0) {
          const org = data[0] as OrganisationWithMembers;

          const index = this.organisations.findIndex(
            (existingOrg) => existingOrg.id === org.id,
          );
          if (index === -1) {
            this.organisations.push(org);
          } else {
            this.organisations[index] = org;
          }

          return org;
        }
      } catch (error) {
        usePosthog().posthogLogError({
          error,
          context: {
            userId: useUserStore().user?.id,
            action: "getOrganisationById",
          },
        });
      }

      return undefined;
    },

    async removeMemberFromOrganisation(
      organisationId: string,
      organisationMemberId: string,
      leavingOrganisation: boolean,
    ) {
      try {
        await $fetch(`/api/organisations/members`, {
          method: HttpMethod.DELETE,
          params: {
            id: organisationId,
            member_id: organisationMemberId,
          },
        });

        if (leavingOrganisation) {
          // If the user is leaving the organisation, we need to remove them from both lists
          this.organisations = this.organisations.filter(
            (org) => org.id !== organisationId,
          );

          this.usersOrganisations = this.usersOrganisations.filter(
            (org) => org.id !== organisationId,
          );
        } else {
          for (const org of this.organisations) {
            if (org.id === organisationId) {
              org.members = org.members?.filter(
                (member) => member.user_id !== organisationMemberId,
              );
            }
          }

          for (const org of this.usersOrganisations) {
            if (org.id === organisationId) {
              org.members = org.members?.filter(
                (member) => member.user_id !== organisationMemberId,
              );
            }
          }
        }
      } catch (error) {
        usePosthog().posthogLogError({
          message: "Failed to remove member from organisation",
          error,
        });
        throw error;
      }
    },

    sortUsersOrganisations() {
      // Sort entries by role. If role is the same, sort alphabetically.
      // 1. Owner
      // 2. Admin
      // 3. Member
      this.usersOrganisations.sort((a, b) => {
        const aOwner = a.members?.some(
          (member) =>
            member.user_id === useUserStore().user?.id &&
            member.role === OrganizationRole.OWNER,
        );
        const bOwner = b.members?.some(
          (member) =>
            member.user_id === useUserStore().user?.id &&
            member.role === OrganizationRole.OWNER,
        );
        if (aOwner && !bOwner) {
          return -1;
        }
        if (!aOwner && bOwner) {
          return 1;
        }

        const aName = a.name;
        const bName = b.name;

        if (aName && bName) {
          return aName.localeCompare(bName);
        }

        return 0;
      });
    },

    getUsersRoleForOrganisation(
      organisationId: string,
    ): OrganizationRole | null {
      const user = useUserStore().user;
      const organisation = this.organisations.find(
        (org) => org.id === organisationId,
      );
      return organisation?.members?.find((m) => m.user_id === user?.id)
        ?.role as OrganizationRole;
    },
  },
});
